import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Card, Divider, Button } from "antd"
import { ReloadOutlined, AudioOutlined } from "@ant-design/icons"
import type React from "react"
import SpeechAssessmentFilterForm from "./components/SpeechAssessmentFilterForm"
import SpeechAssessmentSummaryCards from "./components/SpeechAssessmentSummaryCards"
import SpeechAssessmentTable from "./components/SpeechAssessmentTable"
import SpeechAssessmentDetailDrawer from "./components/SpeechAssessmentDetailDrawer"
import {
  speechAssessmentService,
  type SpeechAssessmentQueryParams,
  type SpeechAssessmentLogItem,
} from "@/services/log/speechAssessmentApi"
import { PAGE_CONFIG, PAGINATION_CONFIG } from "@/constants/table"
import { useTableParams } from "@/hooks/useTableParams"
import dayjs from "dayjs"

export const SpeechAssessmentLogPage: React.FC = () => {
  const [searchParams, setSearchParams] = useTableParams<SpeechAssessmentQueryParams>({
    ...PAGE_CONFIG,
    reference_text: "",
    user_search: "",
    status: "",
    score_range: "",
    start_date: dayjs().subtract(7, "day").format("YYYY-MM-DD"),
    end_date: dayjs().format("YYYY-MM-DD"),
  })

  const [selectedRecord, setSelectedRecord] = useState<SpeechAssessmentLogItem | null>(null)

  const {
    data: result,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["speech_assessment_logs_list", searchParams],
    queryFn: () => {
      return speechAssessmentService.list({
        page: searchParams.page,
        limit: searchParams.per_page,
        reference_text: searchParams.reference_text || undefined,
        user_search: searchParams.user_search || undefined,
        status: searchParams.status || undefined,
        score_range: searchParams.score_range || undefined,
        start_date: searchParams.start_date || undefined,
        end_date: searchParams.end_date || undefined,
      })
    },
  })

  const handleSearch = (values: Omit<SpeechAssessmentQueryParams, "page" | "limit">) => {
    setSearchParams({
      ...searchParams,
      ...values,
      page: 1,
    })
  }

  const items = result?.data || []
  const totalCount = result?.total ?? 0
  const stats = result?.stats

  return (
    <div className="flex flex-col gap-2">
      {/* 4 thẻ thống kê tổng quan */}
      <SpeechAssessmentSummaryCards stats={stats} loading={isFetching} />

      {/* Bộ lọc tìm kiếm */}
      <SpeechAssessmentFilterForm
        onSearch={handleSearch}
        loading={isFetching}
        initialValues={{
          reference_text: searchParams.reference_text,
          user_search: searchParams.user_search,
          status: searchParams.status,
          score_range: searchParams.score_range,
          start_date: searchParams.start_date,
          end_date: searchParams.end_date,
        }}
      />

      {/* Bảng dữ liệu */}
      <Card
        className="flex min-h-0 grow flex-col shadow-xs"
        classNames={{ body: "flex grow flex-col" }}
        title={
          <div className="flex items-center justify-between py-1">
            <div className="flex items-center gap-2">
              <AudioOutlined className="text-blue-600 text-lg" />
              <span className="font-semibold text-base text-gray-800">
                Nhật ký chấm phát âm (Speech Assessment Logs)
              </span>
              <Divider orientation="vertical" />
              <span className="font-normal text-gray-500 text-sm">
                Tổng số bản ghi: <strong className="text-gray-700">{totalCount}</strong>
              </span>
            </div>
            <Button icon={<ReloadOutlined spin={isFetching} />} onClick={() => refetch()} loading={isFetching}>
              Làm mới
            </Button>
          </div>
        }
      >
        <SpeechAssessmentTable
          data={items}
          loading={isFetching}
          onViewDetail={(record) => setSelectedRecord(record)}
          pagination={{
            pageSize: searchParams.per_page,
            current: searchParams.page,
            total: totalCount,
            ...PAGINATION_CONFIG,
            onChange(page: number, pageSize: number) {
              setSearchParams({
                ...searchParams,
                page,
                per_page: pageSize,
              })
            },
          }}
        />
      </Card>

      {/* Drawer xem chi tiết */}
      <SpeechAssessmentDetailDrawer
        open={!!selectedRecord}
        record={selectedRecord}
        onClose={() => setSelectedRecord(null)}
      />
    </div>
  )
}

export default SpeechAssessmentLogPage
